import {useEffect, useState} from 'react';
import {Text, Icon} from '@/ui-kits';
import {Medication} from '@/interface';

interface MedicationsClinicalNoteProps {
  index: number;
  label?: string;
  medications: Medication[];
  onChange: (medications: Medication[]) => void;
  isReadonly?: boolean;
  isGlobalEditing?: boolean;
  onSetIsEditing?: () => void;
}

export const MedicationsClinicalNote = (props: MedicationsClinicalNoteProps) => {
  const {index, label = 'Medications', medications, onChange, isReadonly = false, isGlobalEditing = false, onSetIsEditing = () => {}} = props;
  const [editingRowIndices, setEditingRowIndices] = useState<number[]>([]);

  const handleAddMedication = () => {
    const newMedication: Medication = {
      id: Date.now(),
      name: '',
      strength: '',
      dosage_instructions: '',
    };
    onChange([...medications, newMedication]);
    setEditingRowIndices(prev => [...prev, medications.length]);
    onSetIsEditing();
  };

  const handleUpdateMedication = (rowIndex: number, field: keyof Medication, value: string) => {
    if (isReadonly) return;
    const updatedMedications = medications.map((medication, index) =>
      index === rowIndex ? {...medication, [field]: value} : medication,
    );
    onChange(updatedMedications);  
    onSetIsEditing();
  };

  const handleRemoveMedication = (rowIndex: number) => {
    if (isReadonly) return;
    const updatedMedications = medications.filter((_, index) => index !== rowIndex);
    onChange(updatedMedications);
    setEditingRowIndices(prev =>
      prev
        .filter(index => index !== rowIndex)
        .map(index => (index > rowIndex ? index - 1 : index)),
    );
    onSetIsEditing();
  };

  const toggleEditMedication = (rowIndex: number) => {
    if (isReadonly) return;
    setEditingRowIndices(prev =>
      prev.includes(rowIndex)
        ? prev.filter(index => index !== rowIndex)
        : [...prev, rowIndex],
    );
    onSetIsEditing();
  };

  useEffect(() => {
    if (!isGlobalEditing) {
      setEditingRowIndices([]);
    }
  }, [isGlobalEditing]);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2 mb-2 sm:mb-3">
        <div className="size-5 sm:size-6 bg-gradient-to-br from-ui_blue to-ui_blue_light text-white rounded-full flex items-center justify-center">
          <Text variant="small" className="!text-xs sm:!text-sm">{index}</Text>
        </div>
        <Text variant="subtitle" className="text-ui_blue font-FigtreeSemiBold !text-base sm:!text-lg">
          {label}
        </Text>
      </div>

      {/* Mobile Card Layout */}
      <div className="flex flex-col gap-3 sm:hidden">
        {medications.length === 0 ? (
          <div className="py-4 px-4 text-center !text-xs text-ui_grey font-FigtreeMedium bg-white border border-ui_input_border rounded-lg">
            No medications added
          </div>
        ) : (
          medications.map((medication, rowIndex) => {
            const isEditing = editingRowIndices.includes(rowIndex);

            return (
              <div key={medication.id ?? `${rowIndex}-${medication.name}`} className="bg-white border border-ui_input_border rounded-lg p-4 flex flex-col gap-3">
                {/* Name */}
                <div className="flex flex-col gap-1">
                  <Text className="font-FigtreeSemiBold !text-xs text-ui_input_border_dark">Name</Text>
                  {isEditing && !isReadonly ? (
                    <input
                      value={medication.name}
                      onChange={event => handleUpdateMedication(rowIndex, 'name', event.target.value)}
                      className="w-full !text-xs text-ui_text_black font-FigtreeMedium border border-ui_input_border_dark focus:outline-none focus:ring-1 focus:ring-ui_input_border_dark rounded px-2 py-1.5 bg-white"
                      style={{borderWidth: '1.54px'}}
                      placeholder="Enter medication name"
                    />
                  ) : (
                    <Text className="!text-xs text-ui_text_black font-FigtreeBold">
                      {medication.name || 'Enter medication name'}
                    </Text>
                  )}
                </div>

                {/* Strength */}
                <div className="flex flex-col gap-1">
                  <Text className="font-FigtreeSemiBold !text-xs text-ui_input_border_dark">Strength</Text>
                  {isEditing && !isReadonly ? (
                    <input
                      value={medication.strength}
                      onChange={event => handleUpdateMedication(rowIndex, 'strength', event.target.value)}
                      className="w-full !text-xs text-ui_text_black font-FigtreeMedium border border-ui_input_border_dark focus:outline-none focus:ring-1 focus:ring-ui_input_border_dark rounded px-2 py-1.5 bg-white"
                      style={{borderWidth: '1.54px'}}
                      placeholder="Enter strength"
                    />
                  ) : (
                    <Text className="!text-xs text-ui_text_black font-FigtreeBold">
                      {medication.strength || 'Enter strength'}
                    </Text>
                  )}
                </div>

                {/* Dosage Instructions */}
                <div className="flex flex-col gap-1">
                  <Text className="font-FigtreeSemiBold !text-xs text-ui_input_border_dark">Dosage Instructions</Text>
                  {isEditing && !isReadonly ? (
                    <input
                      value={medication.dosage_instructions}
                      onChange={event =>
                        handleUpdateMedication(rowIndex, 'dosage_instructions', event.target.value)
                      }
                      className="w-full !text-xs text-ui_text_black font-FigtreeMedium border border-ui_input_border_dark focus:outline-none focus:ring-1 focus:ring-ui_input_border_dark rounded px-2 py-1.5 bg-white"
                      style={{borderWidth: '1.54px'}}
                      placeholder="Enter dosage instructions"
                    />
                  ) : (
                    <Text className="!text-xs text-ui_text_black font-FigtreeBold">
                      {medication.dosage_instructions || 'Enter dosage instructions'}
                    </Text>
                  )}
                </div>

                {/* Actions */}
                {!isReadonly && (
                  <div className="flex flex-col gap-1">
                    <Text className="font-FigtreeSemiBold !text-xs text-ui_input_border_dark">Actions</Text>
                    <div className="flex items-center gap-4">
                      {!isEditing ? (
                        <>
                          <button
                            onClick={() => toggleEditMedication(rowIndex)}
                            className="cursor-pointer">
                            <Icon type="edit_input" className="size-4" />
                          </button>
                          <button onClick={() => handleRemoveMedication(rowIndex)} className="cursor-pointer">
                            <Icon type="trash_red" className="size-5" />
                          </button>
                        </>
                      ) : (
                        <button onClick={() => handleRemoveMedication(rowIndex)} className="cursor-pointer">
                          <Icon type="trash_red" className="size-5" />
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Desktop Table Layout */}
      <div className="hidden sm:block border border-ui_input_border rounded-lg overflow-x-auto bg-white">
        <table className="w-full table-fixed">
          <colgroup>
            <col className="w-[32%]" />
            <col className="w-[22%]" />
            <col className="w-[31%]" />
            {!isReadonly && <col className="w-[15%]" />}
          </colgroup>
          <thead className="bg-white border-b-[1.5px] border-ui_input_border_dark">
            <tr>
              <th className="text-left py-2 sm:py-3 px-2 sm:px-4 font-FigtreeSemiBold !text-xs sm:!text-sm text-ui_input_border_dark">
                Name
              </th>
              <th className="text-left py-2 sm:py-3 px-2 sm:px-4 font-FigtreeSemiBold !text-xs sm:!text-sm text-ui_input_border_dark">
                Strength
              </th>
              <th className="text-left py-2 sm:py-3 px-2 sm:px-4 font-FigtreeSemiBold !text-xs sm:!text-sm text-ui_input_border_dark">
                Dosage Instructions
              </th>
              {!isReadonly && (
                <th className="py-2 sm:py-3 px-2 sm:px-4 font-FigtreeSemiBold !text-xs sm:!text-sm text-ui_input_border_dark text-center">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {medications.length === 0 ? (
              <tr>
                <td colSpan={4} className="py-4 sm:py-6 px-2 sm:px-4 text-center !text-xs sm:!text-base text-ui_grey font-FigtreeMedium">
                  No medications added
                </td>
              </tr>
            ) : (
              medications.map((medication, rowIndex) => {
                const isEditing = editingRowIndices.includes(rowIndex);

                return (
                  <tr
                    key={medication.id ?? `${rowIndex}-${medication.name}`}
                    className={rowIndex !== medications.length - 1 ? 'border-b border-ui_input_border' : ''}>
                    <td className="py-2 sm:py-3 px-2 sm:px-4">
                      {isEditing && !isReadonly ? (
                        <input
                          value={medication.name}
                          onChange={event => handleUpdateMedication(rowIndex, 'name', event.target.value)}
                          className="w-full !text-xs sm:!text-base text-ui_text_black font-FigtreeMedium border border-ui_input_border_dark focus:outline-none focus:ring-1 focus:ring-ui_input_border_dark rounded px-2 py-1.5 sm:py-2 bg-white"
                          style={{borderWidth: '1.54px'}}
                          placeholder="Enter medication name"
                        />
                      ) : (
                        <div className="w-full px-2 py-1.5 sm:py-2 !text-xs sm:!text-base text-ui_text_black font-FigtreeMedium">
                          {medication.name || 'Enter medication name'}
                        </div>
                      )}
                    </td>
                    <td className="py-2 sm:py-3 px-2 sm:px-4">
                      {isEditing && !isReadonly ? (
                        <input
                          value={medication.strength}
                          onChange={event => handleUpdateMedication(rowIndex, 'strength', event.target.value)}
                          className="w-full !text-xs sm:!text-base text-ui_text_black font-FigtreeMedium border border-ui_input_border_dark focus:outline-none focus:ring-1 focus:ring-ui_input_border_dark rounded px-2 py-1.5 sm:py-2 bg-white"
                          style={{borderWidth: '1.54px'}}
                          placeholder="Enter strength"
                        />
                      ) : (
                        <div className="w-full px-2 py-1.5 sm:py-2 !text-xs sm:!text-base text-ui_text_black font-FigtreeMedium">
                          {medication.strength || 'Enter strength'}
                        </div>
                      )}
                    </td>
                    <td className="py-2 sm:py-3 px-2 sm:px-4">
                      {isEditing && !isReadonly ? (
                        <input
                          value={medication.dosage_instructions}
                          onChange={event =>
                            handleUpdateMedication(rowIndex, 'dosage_instructions', event.target.value)
                          }
                          className="w-full !text-xs sm:!text-base text-ui_text_black font-FigtreeMedium border border-ui_input_border_dark focus:outline-none focus:ring-1 focus:ring-ui_input_border_dark rounded px-2 py-1.5 sm:py-2 bg-white"
                          style={{borderWidth: '1.54px'}}
                          placeholder="Enter dosage instructions"
                        />
                      ) : (
                        <div className="w-full px-2 py-1.5 sm:py-2 !text-xs sm:!text-base text-ui_text_black font-FigtreeMedium">
                          {medication.dosage_instructions || 'Enter dosage instructions'}
                        </div>
                      )}
                    </td>
                    {!isReadonly && (
                    <td className="py-2 sm:py-3 px-2 sm:px-4">
                      <div className="flex items-center justify-center gap-2 sm:gap-4">
                      <button
                        onClick={() => toggleEditMedication(rowIndex)}
                        className={`cursor-pointer ${isEditing ? 'invisible pointer-events-none' : ''}`}>
                        <Icon type="edit_input" className="size-3.5 sm:size-4" />
                      </button>
                      <button onClick={() => handleRemoveMedication(rowIndex)} className="cursor-pointer">
                          <Icon type="trash_red" className="size-4 sm:size-5" />
                        </button>
                      </div>
                    </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="flex justify-end">
        {!isReadonly && (
        <button
          onClick={handleAddMedication}
          className="mt-2 sm:mt-3 text-ui_blue font-FigtreeSemiBold !text-xs sm:!text-sm flex items-center gap-1">
          <span className="inline-block">+</span>
          <span className="underline font-Figtree font-semibold !text-xs sm:!text-md">Add New Medication</span>
        </button>
        )}
      </div>
    </div>
  );
};

